import React, {
    useMemo,
    Fragment,
    useEffect,
    useCallback,
    useState
} from 'react'
import { SubCategories, FilterBlock } from './FilterElements'
import { ApplyFilter, FiltersProps } from './FilterTypes'
import { getApplyFilterHandler } from './util/getApplyFilterHandler'
import { SidebarFilter } from './SidebarFilter'
import { HorizontalFilter } from './HorizontalFilter'
import { getAvailableAggregationValues } from './util/getAvailableAggregationValues'
import { useTranslation } from 'next-i18next/pages'
import { Cross } from '@components/icons'
import s from './Filters.module.scss'
import { getApplyCriterionHandler } from './util/getApplyCriterionHandler'
import { formatPriceLabel } from './FilterElements/FilterList/formatPriceLabel'
import { getConvertFilterDataToFilterHandler } from './util/convertFilterDataToFilterHandler'
import { Maybe } from '@pylot-data/pylotschema'

export const Filters = (props: FiltersProps): JSX.Element => {
    const {
        aggregations,
        excludeFilter = [],
        category,
        isAccordion = true,
        header,
        isLoading = false,
        showCategory,
        showCount = true,
        currencyCode = 'USD', // TODO: pull from config?
        plpManager,
        isHorizontal = true,
        className,
        iconOpen,
        iconClose,
        showFilterCount = false
    } = props
    const { plpState, setAggregations, setFiltersVisible } = plpManager
    const { t } = useTranslation(['plp'])
    const [initialFilterAttribute, setInitialFilterAttribute] = useState<
        string | null
    >(null)

    const applyFilter: ApplyFilter = useCallback(
        (filterData, shouldApply, isSingleSelect) => {
            if (filterData?.requestVar) {
                setInitialFilterAttribute((prev) =>
                    prev ? prev : filterData.requestVar
                )
            }

            getApplyCriterionHandler(plpManager)(
                filterData,
                shouldApply,
                isSingleSelect
            )
            getApplyFilterHandler(plpManager)(
                filterData,
                shouldApply,
                isSingleSelect
            )
        },
        [plpManager]
    )

    // The first available list of aggregations is used to display filter options onwards
    useEffect(() => {
        if (
            ((category && category.uid !== plpManager.plpState.categoryUid) ||
                !plpState.aggregations ||
                plpState.aggregations.length === 0) &&
            aggregations
        ) {
            setAggregations(aggregations)
        }
    }, [
        aggregations,
        plpState.aggregations,
        setAggregations,
        category,
        plpManager.plpState.categoryUid
    ])

    useEffect(() => {
        if (
            !plpState.appliedFilterData ||
            plpState.appliedFilterData.length === 0
        ) {
            setInitialFilterAttribute(null)
        }
    }, [plpState.appliedFilterData])

    // Attribute values from the latest loaded collection (define enabled filter options)
    const availableValues = useMemo(
        () => getAvailableAggregationValues(aggregations),
        [aggregations]
    )

    const aggregationList = useMemo(() => {
        const allAggregations = plpState.aggregations ?? aggregations ?? []
        return allAggregations.filter(
            (attribute: any) => attribute.attribute_code !== 'categories'
        )
    }, [plpState.aggregations, aggregations])

    const convertCriterionToFilter = useMemo(
        () => getConvertFilterDataToFilterHandler(aggregationList),
        [aggregationList]
    )

    const filtersList = useMemo(
        () =>
            aggregationList
                .sort((a, b) =>
                    a && b
                        ? a.attribute_code.localeCompare(b.attribute_code)
                        : 0
                )
                .map((item, index) => {
                    return !item ? undefined : (
                        <Fragment key={index}>
                            {!excludeFilter.includes(item.attribute_code) && (
                                <FilterBlock
                                    name={item.label}
                                    items={item.options}
                                    availableValues={
                                        availableValues[item.attribute_code] ??
                                        []
                                    }
                                    requestVar={item.attribute_code}
                                    index={index}
                                    isHorizontal={isHorizontal}
                                    applyFilter={applyFilter}
                                    isLoading={isLoading}
                                    showCount={showCount}
                                    currencyCode={currencyCode}
                                    appliedSearchCritieria={
                                        plpState.appliedSearchCritieria
                                    }
                                    appliedFilterData={
                                        plpState.appliedFilterData
                                    }
                                    iconOpen={iconOpen}
                                    iconClose={iconClose}
                                    showFilterCount={showFilterCount}
                                    initialFilterAttribute={
                                        initialFilterAttribute
                                    }
                                />
                            )}
                        </Fragment>
                    )
                }),
        [
            plpState.appliedSearchCritieria,
            aggregationList,
            excludeFilter,
            availableValues,
            isHorizontal,
            applyFilter,
            isLoading,
            showCount,
            currencyCode
        ]
    )

    const getLabel = (
        requestVar: string,
        value_string: string,
        label?: Maybe<string>
    ) => {
        if (label == undefined && requestVar == 'price') {
            return value_string.replace('_', '-')
        }
        return label
    }

    const appliedFilterList = useMemo(
        () =>
            plpState.appliedFilterData
                .map(convertCriterionToFilter)
                .map((item, idx) => {
                    console.log(item)

                    const label =
                        item.requestVar === 'price'
                            ? formatPriceLabel(
                                  t,
                                  getLabel(
                                      item.requestVar,
                                      item.value_string,
                                      item.label
                                  ),
                                  currencyCode
                              )
                            : item.label

                    return (
                        <div className={s['selected-filter']} key={idx}>
                            <span className={s['filter-name']}>{label}</span>
                            <span
                                role="button"
                                tabIndex={idx}
                                className={s['clear-filter']}
                                key={`${item.requestVar}${item.value_string}`}
                                onClick={() => applyFilter(item, false)}
                                onKeyDown={() => applyFilter(item, false)}
                            >
                                <Cross width="18" height="18" />
                            </span>
                        </div>
                    )
                }),
        [applyFilter, convertCriterionToFilter, plpState.appliedSearchCritieria]
    )

    const subCategories = showCategory && category && (
        <SubCategories category={category} applyFilter={applyFilter} />
    )

    const WrapperComponent = isHorizontal ? HorizontalFilter : SidebarFilter

    return (
        <WrapperComponent
            subCategories={subCategories}
            header={header}
            filtersApplied={plpState.appliedSearchCritieria.length}
            applyFilter={applyFilter}
            filtersVisible={plpState.filtersVisible}
            setFiltersVisible={setFiltersVisible}
            isAccordion={isAccordion}
            filtersList={filtersList}
            plpState={plpState}
            appliedFilterList={appliedFilterList}
            className={className}
        />
    )
}
